import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Play, Video } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  en: {
    title: "Video Gallery",
    subtitle: "Watch testimonials, product presentations and more",
    all: "All",
    testimonials: "Testimonials",
    products: "Products",
    story: "My Story",
    noVideos: "No videos available yet"
  },
  ro: {
    title: "Galerie Video",
    subtitle: "Urmărește testimoniale, prezentări de produse și multe altele",
    all: "Toate",
    testimonials: "Testimoniale",
    products: "Produse",
    story: "Povestea Mea",
    noVideos: "Nu există video-uri încă"
  },
  es: {
    title: "Galería de Videos",
    subtitle: "Mira testimonios, presentaciones de productos y más",
    all: "Todos",
    testimonials: "Testimonios",
    products: "Productos",
    story: "Mi Historia",
    noVideos: "No hay videos disponibles"
  },
  it: {
    title: "Galleria Video",
    subtitle: "Guarda testimonianze, presentazioni di prodotti e altro",
    all: "Tutti",
    testimonials: "Testimonianze",
    products: "Prodotti",
    story: "La Mia Storia",
    noVideos: "Nessun video disponibile"
  },
  de: {
    title: "Video-Galerie",
    subtitle: "Sehen Sie Testimonials, Produktpräsentationen und mehr",
    all: "Alle",
    testimonials: "Testimonials",
    products: "Produkte",
    story: "Meine Geschichte",
    noVideos: "Noch keine Videos verfügbar"
  }
};

const VideosPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations['en'];
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/videos?display_on=gallery`);
        setVideos(response.data || []);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const filteredVideos = filter === 'all' 
    ? videos 
    : videos.filter(v => v.category === filter);

  const getEmbedUrl = (video) => {
    if (video.video_type === 'youtube') {
      const match = video.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
      if (match) return `https://www.youtube.com/embed/${match[1]}`;
    }
    return video.video_url;
  };

  const getThumbnail = (video) => {
    if (video.thumbnail_url) return video.thumbnail_url;
    if (video.video_type === 'youtube') {
      const match = video.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
      if (match) return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
    return null;
  };

  const filters = [
    { id: 'all', label: t.all },
    { id: 'testimonial', label: t.testimonials },
    { id: 'product', label: t.products },
    { id: 'story', label: t.story }
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="videos-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" data-testid="videos-page-title">{t.title}</h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${
                filter === f.id
                  ? 'bg-sky-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Videos Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64" data-testid="videos-loading">
            <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="text-center py-16" data-testid="videos-empty">
            <Video className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{t.noVideos}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
            {filteredVideos.map((video) => (
              <div 
                key={video.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`video-card-${video.id}`}
              >
                {/* Video Player or Thumbnail */}
                <div className="aspect-video bg-slate-100 relative">
                  {/* YouTube embed when playing */}
                  {playingVideo === video.id && video.video_type === 'youtube' ? (
                    <iframe
                      src={`${getEmbedUrl(video)}?autoplay=1`}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : /* Native video player for uploaded videos */
                  playingVideo === video.id && (video.video_type === 'upload' || video.video_type === 'link') ? (
                    <video
                      src={video.video_url}
                      controls
                      autoPlay
                      className="w-full h-full object-contain bg-black"
                      onEnded={() => setPlayingVideo(null)}
                    />
                  ) : (
                    <>
                      {/* Thumbnail image or video preview */}
                      {getThumbnail(video) ? (
                        <img 
                          src={getThumbnail(video)} 
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : video.video_type === 'upload' || video.video_type === 'link' ? (
                        <video 
                          src={video.video_url} 
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-200">
                          <Video className="w-12 h-12 text-slate-400" />
                        </div>
                      )}
                      
                      {/* Play button overlay */}
                      <button
                        onClick={() => {
                          if (video.video_type === 'youtube' || video.video_type === 'upload' || video.video_type === 'link') {
                            setPlayingVideo(video.id);
                          } else {
                            // TikTok/Instagram - open in new tab
                            window.open(video.video_url, '_blank');
                          }
                        }}
                        className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                      >
                        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                          <Play className="w-8 h-8 text-sky-600 fill-sky-600 ml-1" />
                        </div>
                      </button>
                    </>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-bold text-slate-900 mb-1">{video.title}</h3>
                  {video.description && (
                    <p className="text-slate-500 text-sm line-clamp-2">{video.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideosPage;
